import { Injectable } from '@angular/core';

import { Api } from '../api/api';
import { environment } from '../environment';
import { IAppCategoryLayer, ICategory, ILayerCatalogItem } from '../types';

import { PetlService } from './petl.service';

const petlDisplayStart = 100;

@Injectable({
    providedIn: 'root'
})
export class LayerCatalogService {

    appCategoriesLayers: IAppCategoryLayer[];
    catalogItems = new Array<ILayerCatalogItem>();
    categories: ICategory[];
    categoryCatalogItemsDictionary: { [key: number]: ILayerCatalogItem } = {};
    unparentedCategories = new Array<ICategory>();

    constructor(private readonly api: Api, private readonly petlService: PetlService) { }

    async getApplicationCategoryLayers(appId: number) {
        return await this.api.http2.graphqlRequest(`
                    query {
                        categories {
                            displayOrder
                            id
                            parentId
                            name
                        }
                        applicationCategoryLayers(applicationId: ${appId}) {
                            categoryId
                            displayOrder
                            layerId
                            layer {
                                id
                                name
                                description
                                isSystem
                            }
                        }
                    }
        `, {});
    }

    async getUserLayers(userId: number) {
        return await this.api.http2.graphqlRequest(`
            query {
                users(id: ${userId}) {
                    userLayers {
                        layer(typeId: 3) {
                        id name typeId sources {id, sourceType}                             
                        }
                    }
                }
            }
        `, {});
    }

    async getUserLayerCatalog(userId: number) {
        const result = await this.getUserLayers(userId);
        const userLayerCatalogItems: ILayerCatalogItem[] = [];
        if (result.users.length > 0) {
            const layers = result.users[0].userLayers.filter(x => x.layer).map(x => x.layer);
            for (const layer of layers) {
                const layerCatalogItem = { name: layer.name, layer };
                userLayerCatalogItems.push(layerCatalogItem);
            }
        }
        return userLayerCatalogItems;
    }

    async generateCatalog(appId: number) {
        await this.fetchCatalogData(appId);
        this.resetCatalogCollections();
        this.placeCategoriesInCatalog();
        this.placeLayersInCatalog();
        this.removeCategoriesWithNoLayers();
        return this.catalogItems;
    }

    async fetchCatalogData(appId: number) {
        const result = await this.getApplicationCategoryLayers(appId);
        this.appCategoriesLayers = result.applicationCategoryLayers.filter(acl => acl.layer !== null && acl.layer.isSystem);
        this.categories = result.categories;
        for (const appCategoryLayer of this.appCategoriesLayers) {
            appCategoryLayer.category = this.categories.find(c => c.id === appCategoryLayer.categoryId);
        }

        const petlCategory = this.categories.find(c => c.id === environment.petlCategoryId);
        if (petlCategory) {
            const petlProjects = await this.petlService.getProjectList();
            const newCatalogItems = petlProjects.map((pr, i) => {
                return {
                    categoryId: petlCategory.id,
                    category: {
                        name: petlCategory.name,
                        id: petlCategory.id
                    },
                    displayOrder: i + petlDisplayStart,
                    layer: {
                        id: pr.id,
                        name: pr.description
                    },
                    layerId: pr.id
                } as IAppCategoryLayer;
            });
            this.appCategoriesLayers.push(...newCatalogItems);
        }
    }

    resetCatalogCollections() {
        this.catalogItems = new Array<ILayerCatalogItem>();
        this.categoryCatalogItemsDictionary = {};
        this.unparentedCategories = this.categories.concat([]);
    }

    placeCategoriesInCatalog() {
        const searchDepthMax = 10;
        let depth = 0;
        while (depth < searchDepthMax && this.getUnparentedCategoriesArray().length > 0) {
            const unparentedCategoriesCopy = this.unparentedCategories.concat([]);
            this.unparentedCategories = [];
            for (const category of unparentedCategoriesCopy) {
                this.placeCategoryInCatalog(category);
            }
            depth++;
        }
    }

    getUnparentedCategoriesArray() {
        return this.unparentedCategories;
    }

    placeLayersInCatalog() {
        for (const appCategoriesLayer of this.appCategoriesLayers) {
            if (appCategoriesLayer.layer) {
                try {
                    const categoryCatalogItem = this.categoryCatalogItemsDictionary[appCategoriesLayer.categoryId];
                    if (!categoryCatalogItem.layerCatalogItems) {
                        categoryCatalogItem.layerCatalogItems = [];
                    }
                    const layerCatalogItem = {
                        displayOrder: appCategoriesLayer.displayOrder,
                        id: appCategoriesLayer.layer.id,
                        name: appCategoriesLayer.layer.name,
                        layer: appCategoriesLayer.layer,
                        description: appCategoriesLayer.layer.description
                    } as ILayerCatalogItem;
                    if (categoryCatalogItem.id === environment.petlCategoryId && layerCatalogItem.displayOrder >= petlDisplayStart) {
                        layerCatalogItem.ispetl = true;
                    }
                    categoryCatalogItem.layerCatalogItems.push(layerCatalogItem);
                } catch (error) {
                    console.error('Error found when creating layer in layer catalog');
                }
            }
        }
    }

    removeCategoriesWithNoLayers() {
        this.catalogItems = this.removeEmptyBranchesAndSort(this.catalogItems);
    }

    removeEmptyBranchesAndSort(list: ILayerCatalogItem[]) {
        const newList: ILayerCatalogItem[] = [];
        for (const item of list) {
            if (item.layerCatalogItems) {
                item.layerCatalogItems = this.removeEmptyBranchesAndSort(item.layerCatalogItems);
            }
            if (item.layer || item.layerCatalogItems) {
                newList.push(item);
            }
        }
        if (newList.length > 0) {
            this.sortLayerCatalogItems(newList);
            return newList;
        } else {
            return null;
        }
    }

    sortLayerCatalogItems(list: ILayerCatalogItem[]) {
        list.sort((a, b) => {
            if (a.displayOrder < b.displayOrder) {
                return -1;
            } else if (a.displayOrder > b.displayOrder) {
                return 1;
            } else {
                return a.name.localeCompare(b.name);
            }
        });
    }

    placeCategoryInCatalog(category: ICategory) {
        let categoryCatalogItem = this.categoryCatalogItemsDictionary[category.id];
        if (!categoryCatalogItem) {
            categoryCatalogItem = { displayOrder: category.displayOrder, id: category.id, name: category.name };
            this.categoryCatalogItemsDictionary[category.id] = categoryCatalogItem;
        }
        if (!category.parentId) {
            this.catalogItems.push(categoryCatalogItem);
        } else {
            const parentCatalogItem = this.categoryCatalogItemsDictionary[category.parentId];
            if (!parentCatalogItem) {
                this.unparentedCategories.push(category);
            } else {
                if (!parentCatalogItem.layerCatalogItems) {
                    parentCatalogItem.layerCatalogItems = [];
                }
                parentCatalogItem.layerCatalogItems.push(categoryCatalogItem);
            }
        }
    }

    async getExternalLayers() {
        return await this.api.http2.graphqlRequest<string>(`
            query {
                mapServers
            }
        `, {}, 'GET', 'mapServers');
    }
}
